import { db, forceSystemNotificationCheck } from "@repo/ui";
import {
	collection,
	doc,
	onSnapshot,
	orderBy,
	query,
	updateDoc,
} from "firebase/firestore";
import {
	Bell,
	Check,
	CheckCircle2,
	Clock,
	Loader2,
	RefreshCw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

interface MonitoringIncident {
	id: string;
	incidentId: string;
	conditionName: string | null;
	incidentUrl: string | null;
	policyName: string | null;
	state: "open" | "closed";
	summary: string | null;
	updatedAt?:
		| Date
		| string
		| number
		| { toDate: () => Date }
		| { seconds: number };
}

export function AlertsPage() {
	const [incidents, setIncidents] = useState<MonitoringIncident[]>([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState<"all" | "open" | "closed">("open");
	const [resolvingId, setResolvingId] = useState<string | null>(null);
	const [checking, setChecking] = useState(false);

	const handleForceCheck = async () => {
		setChecking(true);
		try {
			const res = await forceSystemNotificationCheck();
			const data = res.data;
			if (data.status === "sent") {
				toast.success(data.message);
			} else {
				toast.warning(data.message);
			}
		} catch (error) {
			console.error("Error forcing system check:", error);
			toast.error(
				`Failed to force system check: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		} finally {
			setChecking(false);
		}
	};

	useEffect(() => {
		const q = query(
			collection(db, "monitoringIncidents"),
			orderBy("updatedAt", "desc"),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const list: MonitoringIncident[] = [];
				snapshot.forEach((docSnap) => {
					list.push({
						id: docSnap.id,
						...(docSnap.data() as Omit<MonitoringIncident, "id">),
					});
				});
				setIncidents(list);
				setLoading(false);
			},
			(error) => {
				console.error("Error subscribing to monitoringIncidents:", error);
				toast.error("Failed to load alerts from database.");
				setLoading(false);
			},
		);

		return () => unsubscribe();
	}, []);

	const handleCloseIncident = async (id: string) => {
		setResolvingId(id);
		try {
			const docRef = doc(db, "monitoringIncidents", id);
			await updateDoc(docRef, {
				state: "closed",
				updatedAt: new Date(),
			});
			toast.success(`Incident ${id} manually closed.`);
		} catch (error) {
			console.error("Error resolving incident:", error);
			toast.error(
				`Failed to close incident: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		} finally {
			setResolvingId(null);
		}
	};

	const filteredIncidents = incidents.filter((incident) => {
		if (filter === "all") return true;
		return incident.state === filter;
	});

	const openCount = incidents.filter((i) => i.state === "open").length;
	const closedCount = incidents.filter((i) => i.state === "closed").length;

	const formatDate = (timestamp: MonitoringIncident["updatedAt"]) => {
		if (!timestamp) return "N/A";
		if (typeof timestamp === "object" && "toDate" in timestamp) {
			return timestamp.toDate().toLocaleString();
		}
		if (typeof timestamp === "object" && "seconds" in timestamp) {
			return new Date(timestamp.seconds * 1000).toLocaleString();
		}
		if (timestamp instanceof Date) return timestamp.toLocaleString();
		return new Date(timestamp).toLocaleString();
	};

	if (loading) {
		return (
			<div className="flex h-96 items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
						<Bell className="h-8 w-8 text-primary" /> System Alerts
					</h1>
					<p className="text-slate-500 mt-1">
						Monitor and manually resolve system alerts that bypass automated
						recovery.
					</p>
				</div>
				<Button
					disabled={checking}
					onClick={handleForceCheck}
					className="bg-primary hover:bg-primary/90 text-white font-medium"
				>
					{checking ? (
						<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
					) : (
						<RefreshCw className="h-4 w-4 mr-1.5" />
					)}
					Force System Check
				</Button>
			</div>

			<div className="admin-alerts-snapshot" aria-label="Alert summary">
				<div>
					<span>Total alerts</span>
					<strong>{incidents.length}</strong>
				</div>
				<div>
					<span>Active / open</span>
					<strong data-urgent={openCount > 0}>{openCount}</strong>
				</div>
				<div>
					<span>Resolved / closed</span>
					<strong>{closedCount}</strong>
				</div>
			</div>

			{/* Filter Tabs and Incident List */}
			<Card className="bg-white border shadow-sm">
				<CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b pb-6">
					<div>
						<CardTitle className="text-lg font-bold">Incident Log</CardTitle>
						<CardDescription>
							List of log-based and manual system alerts.
						</CardDescription>
					</div>
					<div className="flex items-center gap-1.5 border p-1 rounded-lg bg-slate-50 self-start">
						<Button
							variant={filter === "open" ? "default" : "ghost"}
							size="sm"
							onClick={() => setFilter("open")}
							className={
								filter === "open"
									? "bg-white text-slate-950 shadow-sm border hover:bg-white"
									: ""
							}
						>
							Open ({openCount})
						</Button>
						<Button
							variant={filter === "closed" ? "default" : "ghost"}
							size="sm"
							onClick={() => setFilter("closed")}
							className={
								filter === "closed"
									? "bg-white text-slate-950 shadow-sm border hover:bg-white"
									: ""
							}
						>
							Closed ({closedCount})
						</Button>
						<Button
							variant={filter === "all" ? "default" : "ghost"}
							size="sm"
							onClick={() => setFilter("all")}
							className={
								filter === "all"
									? "bg-white text-slate-950 shadow-sm border hover:bg-white"
									: ""
							}
						>
							All
						</Button>
					</div>
				</CardHeader>
				<CardContent className="pt-6">
					{filteredIncidents.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-slate-400">
							<CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
							<p className="font-medium text-slate-600 text-lg">
								All systems operational
							</p>
							<p className="text-sm mt-1 text-slate-500">
								No alerts match the current filter.
							</p>
						</div>
					) : (
						<>
							<p className="mb-3 text-xs text-muted-foreground">
								Scroll the incident log horizontally to inspect long summaries
								and actions.
							</p>
							<Table className="min-w-[1300px] table-fixed">
								<TableHeader>
									<TableRow>
										<TableHead className="w-[100px]">State</TableHead>
										<TableHead className="w-[160px]">Incident ID</TableHead>
										<TableHead className="w-[260px]">
											Policy & Condition
										</TableHead>
										<TableHead className="w-[440px] whitespace-normal break-words">
											Summary
										</TableHead>
										<TableHead className="w-[180px]">Updated At</TableHead>
										<TableHead className="w-[160px] text-right">
											Action
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{filteredIncidents.map((incident) => (
										<TableRow
											key={incident.id}
											className="hover:bg-slate-50/50"
										>
											<TableCell>
												<Badge
													variant={
														incident.state === "open"
															? "destructive"
															: "secondary"
													}
													className={
														incident.state === "closed"
															? "bg-green-50 text-green-700 border-green-200"
															: ""
													}
												>
													{incident.state === "open" ? (
														<span className="flex items-center gap-1">
															<span className="h-1.5 w-1.5 rounded-full bg-white animate-ping" />
															Open
														</span>
													) : (
														"Closed"
													)}
												</Badge>
											</TableCell>
											<TableCell className="font-mono text-xs text-slate-600">
												{incident.incidentId}
											</TableCell>
											<TableCell className="whitespace-normal break-words [overflow-wrap:anywhere]">
												<div className="font-medium text-slate-900">
													{incident.policyName || "N/A"}
												</div>
												<div className="text-xs text-slate-500 mt-0.5">
													{incident.conditionName || "N/A"}
												</div>
											</TableCell>
											<TableCell className="whitespace-normal break-words text-slate-600 text-xs [overflow-wrap:anywhere]">
												{incident.summary || "No description provided."}
												{incident.incidentUrl && (
													<div className="mt-1">
														<a
															href={incident.incidentUrl}
															target="_blank"
															rel="noopener noreferrer"
															className="text-xs text-primary hover:underline inline-flex items-center gap-1"
														>
															View incident in GCP
														</a>
													</div>
												)}
											</TableCell>
											<TableCell className="text-xs text-slate-600 font-medium">
												<div className="flex items-center gap-1.5">
													<Clock className="h-3.5 w-3.5 text-slate-400" />
													{formatDate(incident.updatedAt)}
												</div>
											</TableCell>
											<TableCell className="text-right">
												{incident.state === "open" ? (
													<Button
														size="sm"
														variant="outline"
														className="border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
														disabled={resolvingId === incident.id}
														onClick={() => handleCloseIncident(incident.id)}
													>
														{resolvingId === incident.id ? (
															<Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
														) : (
															<Check className="h-3.5 w-3.5 mr-1 text-green-600" />
														)}
														Close Alert
													</Button>
												) : (
													<span className="text-xs text-slate-400 flex items-center justify-end gap-1 font-medium">
														<Check className="h-4 w-4 text-green-600" />{" "}
														Resolved
													</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
